import { ValidatorFn, AbstractControl } from "@angular/forms";
import { CasingPlanModel, WellSegmentModel, CasingType, WellDirection, CasingTypeModel } from "../models/casing-plan.model";
import { properCaseString } from "../models/casing-plan-functions.model";
import { Alert, EORAlert, NonMappableUSDWAlert, NonMappableUndergroundWaterAlert, ClintonPinchoutAlert, DriftThicknessAlert, DefinedHorizontalWellAlert } from "../models/Alerts.model";
import { isDefined } from "./casing-plan-functions.model";

export function casingShoeLengthValidator(segment: WellSegmentModel): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        if (isDefined(segment.casingBottom) && isDefined(segment.casingTop) && isDefined(segment.casingShoeDepth)) {
            const casingLength = segment.casingBottom - segment.casingTop;
            if (segment.casingShoeDepth > casingLength) {
                msg = "Shoe length must be equal to or less than Casing length.";
            }           
        }
        return (msg.length > 0) ? { "casingShoeLength": { value: msg } } : null;
    } 
}

export function casingVsBoreholeDiameter(segment: WellSegmentModel): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        let diameterDiff = 2;
        if (isDefined(segment.casingDiameter) && isDefined(segment.boreholeDiameter) && !segment.toBePulled) {
            if (segment.boreholeDiameter - segment.casingDiameter < diameterDiff) {
                msg = `Casing Diameter must be at least ${diameterDiff}" less than Borehole diameter.`;
            }
        }
        return (msg.length > 0) ? { "casingDiameter": { value: msg } } : null;
    }
}

export function casingVsBoreholeLength(segment: WellSegmentModel): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        if (isDefined(segment.casingBottom) && isDefined(segment.casingTop) && isDefined(segment.boreholeBottom) && isDefined(segment.boreholeTop)) {
            const boreholeLength = segment.boreholeBottom - segment.boreholeTop;
            const casingLength = segment.casingBottom - segment.boreholeTop;
            if (boreholeLength < casingLength) {
                msg = "Casing length must be less than or equal to borehole length.";
            }
        }
        return (msg.length > 0) ? { "casingLength": { value: msg } } : null;
    }
}

export function cementBottomToBoreholeBottom(segment: WellSegmentModel): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        if (isDefined(segment.boreholeBottom) && isDefined(segment.cementBottom)) {
            if (segment.boreholeBottom < segment.cementBottom) {
                msg = "Cement bottom cannot be below the borehole bottom.";
            }
        }
        return (msg.length > 0) ? { "cementBottom": { value: msg } } : null;
    }
}

export function concentricToolDiameter(segment: WellSegmentModel): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        if (isDefined(segment.boreholeDiameter && isDefined(segment.casingDiameter))) {
            if (segment.boreholeDiameter !== segment.casingDiameter) {
                msg = "Casing Diameter must equal Borehole Diameter when using a Concentric drilling tool.";
            }
        }
        return (msg.length > 0) ? { "concentricToolDiameter": { value: msg } } : null;
    }
}

export function drivePipeDiameter(segment: WellSegmentModel): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        if (isDefined(segment.boreholeDiameter && isDefined(segment.casingDiameter))) {
            if (segment.boreholeDiameter !== segment.casingDiameter) {
                msg = "Drive Pipe casing diameter should be equal to its borehole diameter.";
            }
        }
        return (msg.length > 0) ? { "drivePipeDiameter": { value: msg } } : null;
    }
}

export function conductorAndSurfaceStringsStartAtTop(currentSegment: WellSegmentModel): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        if (isDefined(currentSegment) && currentSegment.casingType.definition == CasingType.Conductor || currentSegment.casingType.definition == CasingType.Surface) {
            if (currentSegment.casingTop != 0) {
                msg = "Conductor and Surface strings must have a Casing Top starting at the surface.";
            }
        }
        return (msg.length > 0) ? { "conductorOrSurfaceCasingTop": { value: msg } } : null;
    }
}

export function conductorAndSurfaceStringsCementedToSurface(currentSegment: WellSegmentModel, previousSegment: WellSegmentModel, field: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        if (isDefined(currentSegment) && currentSegment.casingType.definition == CasingType.Conductor || currentSegment.casingType.definition == CasingType.Surface) {
            if (field === "cementTop") {
                if (isDefined(currentSegment.cementTop) && currentSegment.cementTop !== 0) {
                    msg = "Conductor and surface strings must be cemented to surface.";
                }
            } else if (field === "cementBottom") {
                if (isDefined(currentSegment.boreholeBottom) && isDefined(currentSegment.cementBottom) && currentSegment.cementBottom !== currentSegment.boreholeBottom) {
                    msg = "Conductor and surface strings must be cemented to surface.";
                }
            }      
        }        
        return (msg.length > 0) ? { "cementToSurface": { value: msg } } : null;
    }
}

export function guideShoeRequired(currentSegment: WellSegmentModel, previousSegment: WellSegmentModel, alerts: Alert[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        const alert = alerts.find(a => a instanceof DriftThicknessAlert);
        if (alert.applicable && isDefined(alert.thickness) && alert.thickness > 100) {
            msg = !isDefined(currentSegment.casingShoeDepth) ? `Glacial drift thickness in area is ${alert.thickness} feet.  A guide shoe is required.` : "";           
        }      
        return (msg.length > 0) ? { "guideShoeRequired": { value: msg } } : null;
    }
}

export function horizontalWell(segment: WellSegmentModel, alerts: Alert[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        let alert = alerts.find(a => a instanceof DefinedHorizontalWellAlert);
        if (isDefined(alert) && alert.applicable) {
            const casingDepth = segment.casingBottom;
            if (casingDepth < 1000) {
                msg = "Intermediate casing string must be at least 1000' deep when drilling a Horizontal Well.";
            }
        }        
        return (msg.length > 0) ? { "horizontalWell": { value: msg } } : null;
    }
}

export function intermediateCementPositiveLength(segment: WellSegmentModel): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        if (!segment.toBePulled) {
            if (segment.cementBottom - segment.cementTop < 0) {
                msg = "Cement must have a positive length.";
            }
        }

        return (msg.length > 0) ? { "intermediateCementLength": { value: msg } } : null;
    }
}

export function intermediateCementRequired(segment: WellSegmentModel, value: string ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let required: boolean = !segment.toBePulled;
        if (required) {
            switch (value) {
                case "CementTop":
                    required = !isDefined(segment.cementTop);
                    break;
                case "CementBottom":
                    required = !isDefined(segment.cementBottom);
                    break;
            }            
        }
        return (required) ? { "intermediateCement": { value: "Non-pulled intermediate strings require cement." } } : null;
    }
}



export function isNumeric(segment: WellSegmentModel, field: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let isValid = true;
        let msg: string = "";
        switch (field) {
            case "boreholeDiameter":
                if (isDefined(segment.boreholeDiameter)) {
                    isValid = !(isNaN(segment.boreholeDiameter) || segment.boreholeDiameter < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }               
                break;
            case "boreholeDepth":
                if (isDefined(segment.boreholeDepth)) {
                    isValid = !(isNaN(segment.boreholeDepth) || segment.boreholeDepth < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "boreholeTop":
                if (isDefined(segment.boreholeTop)) {
                    isValid = !(isNaN(segment.boreholeTop) || segment.boreholeTop < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "boreholeBottom":
                if (isDefined(segment.boreholeBottom)) {
                    isValid = !(isNaN(segment.boreholeBottom) || segment.boreholeBottom < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "casingDiameter":
                if (isDefined(segment.casingDiameter)) {
                    isValid = !(isNaN(segment.casingDiameter) || segment.casingDiameter < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "casingTop":
                if (isDefined(segment.casingTop)) {
                    isValid = !(isNaN(segment.casingTop) || segment.casingTop < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "casingBottom":
                if (isDefined(segment.casingBottom)) {
                    isValid = !(isNaN(segment.casingBottom) || segment.casingBottom < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "casingLength":
                if (isDefined(segment.casingLength)) {
                    isValid = !(isNaN(segment.casingLength) || segment.casingLength < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "casingWeight":
                if (isDefined(segment.casingWeight)) {
                    isValid = !(isNaN(segment.casingWeight) || segment.casingWeight < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "casingShoeDepth":
                if (isDefined(segment.casingShoeDepth)) {
                    isValid = !(isNaN(segment.casingShoeDepth) || segment.casingShoeDepth < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "numberCentralizers":
                if (isDefined(segment.numberCentralizers)) {
                    isValid = !(isNaN(segment.numberCentralizers) || segment.numberCentralizers < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "floatCollarDepth":
                if (isDefined(segment.floatCollarDepth)) {
                    isValid = !(isNaN(segment.floatCollarDepth) || segment.floatCollarDepth < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "basketDepth":
                if (isDefined(segment.basketDepth)) {
                    isValid = !(isNaN(segment.basketDepth) || segment.basketDepth < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "packerDepth":
                if (isDefined(segment.packerDepth)) {
                    isValid = !(isNaN(segment.packerDepth) || segment.packerDepth < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "deviationToolDepth":
                if (isDefined(segment.dvToolDepth)) {
                    isValid = !(isNaN(segment.dvToolDepth) || segment.dvToolDepth < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "cementTop":
                if (isDefined(segment.cementTop)) {
                    isValid = !(isNaN(segment.cementTop) || segment.cementTop < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "cementBottom":
                if (isDefined(segment.cementBottom)) {
                    isValid = !(isNaN(segment.cementBottom) || segment.cementBottom < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "perforationTop":
                if (isDefined(segment.perforationTop)) {
                    isValid = !(isNaN(segment.perforationTop) || segment.perforationTop < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "perforationBottom":
                if (isDefined(segment.perforationBottom)) {
                    isValid = !(isNaN(segment.perforationBottom) || segment.perforationBottom < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "stageNumber":
                if (isDefined(segment.stageNumber)) {
                    isValid = !(isNaN(segment.stageNumber) || segment.stageNumber < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "numberOfStages":
                if (isDefined(segment.numberOfStages)) {
                    isValid = !(isNaN(segment.numberOfStages) || segment.numberOfStages < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;
            case "shotsPerStage":
                if (isDefined(segment.shotsPerStage)) {
                    isValid = !(isNaN(segment.shotsPerStage) || segment.shotsPerStage < 0);
                    if (!isValid) {
                        msg = `${properCaseString(field)} must be a properly formatted positive number.`;
                    }
                }      
                break;            
        }
        return (!isValid && msg.length > 0) ? { "fieldNumeric": { value: msg } } : null;
    }
}

export function maximumTotalDepth(plan: CasingPlanModel, segment: WellSegmentModel, name: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        let field: number = undefined;
        switch (name) {
            case "Borehole":                
                field = segment.boreholeBottom;
                break;
            case "Casing":
                field = segment.casingBottom;
                break;
            case "Cement":
                field = segment.cementBottom;
                break;
        }
        if (isDefined(field)) {
            if (field > plan.proposedTotalDepth) {
                msg = `${name} cannot exceed the proposed total depth (${plan.proposedTotalDepth} ft.) of the well.`;
            }
        }

        return (msg.length > 0) ? { "totalDepth": { value: msg } } : null;
    }
}

export function minimumCasingDimension(segment: WellSegmentModel, alerts: Alert[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";       
        for (let a = 0; a < alerts.length; a++) {
            if (alerts[a] instanceof NonMappableUSDWAlert && segment.casingType.definition === CasingType.Conductor) {
                if (alerts[a].applicable) {
                    const casingLength = segment.casingBottom - segment.casingTop;
                    if (casingLength < 100) {
                        msg = "Conductor must be at least 100' long due to non-mappable USDW conditions.";
                    }
                }
            } else if (alerts[a] instanceof NonMappableUndergroundWaterAlert && segment.casingType.definition === CasingType.Surface) {
                if (alerts[a].applicable) {
                    const casingDepth = segment.casingBottom;
                    if (casingDepth < 300) {
                        msg = "Surface Casing must be at least 300' deep due to non-mappable USDW conditions.";
                    }
                }
            } else if (alerts[a] instanceof ClintonPinchoutAlert && (segment.casingType.definition == CasingType.Intermediate1 || segment.casingType.definition === CasingType.Intermediate2)) {
                if (alerts[a].applicable) {
                    const casingDepth = segment.casingBottom;
                    if (casingDepth < 1000) {
                        msg = "Intermediate casing string must be at least 1000' deep due to Clinton Pinchout conditions.";
                    }
                }
            }
        }
        return (msg !== "") ? { "casingLength": { value: msg } } : null;
    }
}

export function packerRequired(segment: WellSegmentModel, alerts: Alert[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let required: boolean = false;
        for (let a = 0; a < alerts.length; a++) {
            if (alerts[a] instanceof EORAlert) {
                if (alerts[a].applicable) {
                    required = segment.packerDepth === undefined || segment.packerDepth === null || segment.packerDepth === 0;
                }
            }
        }
        return (required) ? { "required": true } : null;
    }
}

export function positiveLength(model: WellSegmentModel, name: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        let field1: number = undefined;
        let field2: number = undefined;
        switch (name) {
            case "Borehole":
                field1 = model.boreholeTop;
                field2 = model.boreholeBottom;
                break;
            case "Casing":
                field1 = model.casingTop;
                field2 = model.casingBottom;
                break;
            case "Cement":
                field1 = model.cementTop;
                field2 = model.cementBottom;
                break;
        }
        if (isDefined(field1) && isDefined(field2)) {
            if (field2 < field1) {
                msg = `${name} must have a positive length.`;
            }
        }
        return (msg.length > 0) ? { name: { value: msg } } : null;
    }
}

export function reconditioned(model: WellSegmentModel, name: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";     
        if (model.casingCondition && model.casingCondition.name && model.casingCondition.name.toLocaleLowerCase() === "reconditioned") {
            if (name === 'accept' && !model.casingCondition.reconditionAccepted) {
                msg = "Reconditioned requirements must be accepted";
            } else if (name === 'acknowledge' && (!isDefined(model.casingCondition.reconditionAcknowledged) || model.casingCondition.reconditionAcknowledged.toLocaleLowerCase() !== 'yes')) {
                msg = "Reconditioned requirements must be acknowleged";
            }
        }
        return (msg.length > 0) ? { name: { value: msg } } : null;
    };
}

export function requireNonDefaultOption(currentSegment: WellSegmentModel, attribute: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        switch (attribute) {
            case "casingType":
                if (!isDefined(currentSegment.casingType) || !isDefined(currentSegment.casingType.code) || currentSegment.casingType.code === "-1") {
                    msg = "Casing Type is Required";
                }
            break;
            case "tool":
                if (!isDefined(currentSegment.drillingEquipment) || !isDefined(currentSegment.drillingEquipment.code) ||  currentSegment.drillingEquipment.code === "-1") {
                    msg = "Tool is Required.";
                }
            break;
            case "condition":
                if (!isDefined(currentSegment.casingCondition) || !isDefined(currentSegment.casingCondition.id) || currentSegment.casingCondition.id === "-1") {
                    msg = "Casing Condition is Required.";
                }
            break;
            case "formation":
                if (!isDefined(currentSegment.formation) || !isDefined(currentSegment.formation.code) || currentSegment.formation.code === "-1") {
                    msg = "Deepest Formation is Required";
                }
            break;
        }
        return (msg.length > 0) ? { name: { value: msg } } : null;
    };
}

export function telescopingSegments(currentSegment: WellSegmentModel, previousSegment: WellSegmentModel, diameterToCheck: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        let msg = "";
        if (previousSegment) {
            switch (diameterToCheck) {
                case "borehole":
                    //If the previous segment has a casing, the borehole needs to be smaller than it, if it's just an open hole, then it can be equal to or less than the previous borehole diameter
                    if (isDefined(previousSegment.casingDiameter) && isDefined(currentSegment.boreholeDiameter)) {
                        if (previousSegment.casingDiameter <= currentSegment.boreholeDiameter) {
                            msg = "Borehole of current string must be smaller in diameter than the outer casing diameter of the previous string.";
                        }
                    }
                    else {
                        if (isDefined(previousSegment.boreholeDiameter) && isDefined(currentSegment.boreholeDiameter)) {
                            if (previousSegment.boreholeDiameter - currentSegment.boreholeDiameter < 0) {
                                msg = "Borehole of current string must be equal to or smaller in diameter than the borehole of the previous string.";
                            }
                        }
                    }
                    break;
                case "casing":
                    if (isDefined(previousSegment.casingDiameter) && isDefined(currentSegment.casingDiameter)) {
                        if (previousSegment.casingDiameter - currentSegment.casingDiameter < 0) {                            
                            msg = (currentSegment.casingType.definition === CasingType.Tubing) 
                                ? "Tubing must be equal to or smaller in diameter than the smallest previous string."
                                : "Casing of current string must be equal to or smaller in diameter than the previous string.";
                        }
                    }                   
                    break;
            }
        }
        return (msg.length > 0) ? { diameterToCheck: { value: msg } } : null;
    }
}

export function tubingLength(plan: CasingPlanModel, segment: WellSegmentModel): ValidatorFn {
    return (control: AbstractControl) : { [key: string]: any } | null => {
        let msg = "";
        if (isDefined(segment.casingBottom)) {            
            const deepestCasing = Math.max.apply(Math, plan.wellSegments.filter(s => isDefined(s.casingBottom)).map((s) => { return s.casingBottom; }));
            const deepestHole = Math.max.apply(Math, plan.wellSegments.filter(h => isDefined(h.boreholeBottom)).map((h) => { return h.boreholeBottom; }));
            if (segment.casingBottom > deepestCasing && segment.casingBottom > deepestHole) {
                msg = `Tubing cannot be longer than the total length of all previous casings (${deepestCasing} ft.) or boreholes (${deepestHole} ft.).`;
            }
        }
        return (msg.length > 0) ? { "tubingLength": { value: msg } } : null;
    }   
}

